/**
 * Seats on a true circle around the bottle, for any player count and screen size.
 *
 * Seat k sits at k * (360 / n) degrees anticlockwise from the top, which is exactly the
 * seat pickPlayerIndex() in game.jsx returns for a bottle rotated clockwise, so the bottle
 * always points at the player it picks.
 *
 * Names are always shown in full: each name pill wraps onto as many lines as it needs, and
 * avatars / pills shrink until no seat overlaps its neighbours.
 *
 * All values are px relative to the game page (the full viewport).
 */

const CHAR_W = 0.75; // generous glyph width of AachenStd-Bold, as a fraction of font size
const LINE_H = 1.15; // matches .td-seat-name line-height
const MAX_LINES = 3; // above this a name gets a smaller font before it gets more lines

// Number of lines `text` wraps to with at most `maxChars` per line (words longer than a line break anywhere).
function countLines(text, maxChars) {
  const words = String(text ?? "").trim().split(/\s+/);
  let lines = 1;
  let cur = 0;
  for (const word of words) {
    let len = word.length;
    if (cur && cur + 1 + len <= maxChars) {
      cur += 1 + len;
      continue;
    }
    if (cur) lines++;
    while (len > maxChars) {
      len -= maxChars;
      lines++;
    }
    cur = len;
  }
  return lines;
}

// Font size, height and width of one name pill inside a seat `slot` px wide.
function measureName(name, slot, isMobile) {
  const baseFont = isMobile ? 9 : 12;
  const minFont = isMobile ? 7 : 9;
  const padX = isMobile ? 10 : 16;
  const padY = isMobile ? 2 : 4;
  const text = String(name ?? "").trim();

  let font = baseFont;
  let lines;
  for (;;) {
    const maxChars = Math.max(Math.floor((slot - padX) / (CHAR_W * font)), 1);
    lines = countLines(text, maxChars);
    if (lines <= MAX_LINES || font <= minFont) break;
    font--;
  }

  const width = lines === 1 ? Math.min(text.length * CHAR_W * font + padX, slot) : slot;
  return { font, width, height: lines * font * LINE_H + padY };
}

const overlaps = (a, b, pad) =>
  a.x1 < b.x2 + pad && b.x1 < a.x2 + pad && a.y1 < b.y2 + pad && b.y1 < a.y2 + pad;

export function computeSeatLayout({ names = [], count, width, height, topReserve, bottomReserve, isMobile }) {
  const n = Math.max(count ?? names.length, 1);
  const maxSlot = isMobile ? 110 : 180; // widest a seat (name pill) may get
  const minSlot = isMobile ? 56 : 80;
  const minAvatar = isMobile ? 30 : 44;
  const nameGap = isMobile ? 8 : 10; // flex gap + the selected pill's extra margin
  const gap = 8;
  const pad = 4; // breathing room between neighbouring seats (and the selected seat's glow)

  const availH = Math.max(height - topReserve - bottomReserve, 160);
  let avatar = isMobile
    ? Math.min(Math.max(height * 0.075, 44), 72)
    : Math.min(Math.max(height * 0.11, 60), 112);
  let slot = maxSlot;

  let names_, nameH, radius, cx, cy, seats;
  const build = () => {
    names_ = Array.from({ length: n }, (_, k) => measureName(names[k], slot, isMobile));
    nameH = Math.max(...names_.map((m) => m.height)) + nameGap;

    // Largest radius that fits the circle (plus avatars and names) on screen.
    radius = Math.max(Math.min((availH - avatar - nameH) / 2, width / 2 - slot / 2 - gap), 40);
    const blockH = 2 * radius + avatar + nameH;
    cx = width / 2;
    cy = topReserve + (availH - blockH) / 2 + avatar / 2 + radius;

    seats = Array.from({ length: n }, (_, k) => {
      const angle = (2 * Math.PI * k) / n; // anticlockwise from the top
      return { x: cx - radius * Math.sin(angle), y: cy - radius * Math.cos(angle) };
    });
  };

  // Each seat is two boxes: the avatar and the name pill under it.
  const boxesOf = (k) => {
    const { x, y } = seats[k];
    const top = y + avatar / 2 + nameGap;
    const w = names_[k].width;
    return [
      { x1: x - avatar / 2, x2: x + avatar / 2, y1: y - avatar / 2 - avatar * 0.12, y2: y + avatar / 2 },
      { x1: x - w / 2, x2: x + w / 2, y1: top, y2: top + names_[k].height },
    ];
  };

  const anyOverlap = () => {
    if (n < 2) return false;
    const boxes = seats.map((_, k) => boxesOf(k));
    for (let i = 0; i < n; i++)
      for (let j = i + 1; j < n; j++)
        for (const a of boxes[i]) for (const b of boxes[j]) if (overlaps(a, b, pad)) return true;
    return false;
  };

  // Shrink avatars first, then pills, until no two seats touch.
  for (let i = 0; i < 60; i++) {
    build();
    if (!anyOverlap()) break;
    if (avatar > minAvatar) avatar = Math.max(avatar * 0.95, minAvatar);
    else if (slot > minSlot) slot = Math.max(slot * 0.95, minSlot);
    else break;
  }

  return { cx, cy, radius, avatar, slot, seats, nameFonts: names_.map((m) => m.font) };
}
